import {
  getAirlines,
  getNonRevAirlines,
  getExcludedAirlines,
  excludeIncludeAirlines,
  getUserProfile,
} from "apis/settingsServices";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useToastStore } from "../../store/toastStore"; // Import the toaster store
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import { GroupBase, StylesConfig } from "react-select";
import { isIOSDevice } from "utils/device";

interface Airline {
  airlineId: number;
  id: number;
  airlineName: string;
  logo: string;
}

interface AirlineOption {
  logo: string;
  value: number;
  label: string;
}

const FlightSearch: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [nonRevAirlines, setNonRevAirlines] = useState<Airline[]>([]);
  const [workingAirlines, setWorkingAirlines] = useState<Airline[]>([]);
  const [excludedAirlines, setExcludedAirlines] = useState<AirlineOption[]>([]);
  const [airlineOptions, setAirlineOptions] = useState<AirlineOption[]>([]);
  const [selectedAirlines, setSelectedAirlines] = useState<AirlineOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { showToast } = useToastStore.getState(); // Get the showToast function from the store

  const customStyles: StylesConfig<
    AirlineOption,
    true,
    GroupBase<AirlineOption>
  > = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#F5F9FE",
      border: "none",
      height: "fit-content",
      minHeight: "50px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#F5F9FE",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#DCEEFF"
        : state.isFocused
          ? "#EAF4FF"
          : "#F5F9FE",
      color: "#000",
    }),
  };
  // Fetch Non-Rev airlines
  useEffect(() => {
    const fetchAirlines = async () => {
      showLoader();
      try {
        const response = await getNonRevAirlines();
        const list: Airline[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];
        setNonRevAirlines(list);
      } catch (error) {
        console.error("Failed to fetch airlines", error);
        setNonRevAirlines([]);
        //showToast('Failed to fetch Non-Rev airlines. Please try again.', 'error'); // Show error toaster
      } finally {
        hideLoader();
      }
    };

    fetchAirlines();
  }, [showToast]);

  // Fetch homebase, primary airline & additional airlines — user's working /
  // priority airlines cannot also be excluded.
  useEffect(() => {
    const fetchWorkingAirlines = async () => {
      try {
        const profileData = await getUserProfile();
        const list: Airline[] = [];
        if (profileData?.primaryAirline) list.push(profileData.primaryAirline);
        if (profileData?.homebase) list.push(profileData.homebase);
        if (Array.isArray(profileData?.additionalAirlines)) {
          list.push(...profileData.additionalAirlines);
        }
        setWorkingAirlines(list);
      } catch (error) {
        console.error("Failed to fetch user profile airlines", error);
        setWorkingAirlines([]);
      }
    };

    fetchWorkingAirlines();
  }, []);

  // Fetch Excluded Airlines (Runs on mount & after save)
  const fetchExcludedAirlines = async () => {
    try {
      const response = await getExcludedAirlines();
      if (response && Array.isArray(response)) {
        const formatted = response.map((airline: Airline) => ({
          value: airline.id,
          label: airline.airlineName,
          logo: airline.logo,
        }));
        setExcludedAirlines(formatted);
      }
    } catch (error) {
      console.error("Failed to fetch excluded airlines", error);
      //showToast('Failed to fetch excluded airlines. Please try again.', 'error'); // Show error toaster
    }
  };

  useEffect(() => {
    fetchExcludedAirlines();
  }, [showToast]);

  // Fetch airline options for search
  const fetchAirlineOptions = async (inputValue: string) => {
    try {
      const response = await getAirlines(inputValue);
      if (response && Array.isArray(response)) {
        const options = response.map((airline: Airline) => ({
          value: airline.id,
          label: airline.airlineName,
          logo: airline.logo,
        }));
        setAirlineOptions(options);
      }
    } catch (error) {
      console.error("Error fetching airlines", error);
      showToast("Error fetching airline options. Please try again.", "error"); // Show error toaster
    }
  };

  const handleSearchChange = (inputValue: string) => {
    fetchAirlineOptions(inputValue);
  };

  // Normalize an airline name for cross-list comparison. The airline search /
  // excluded endpoints return names with the IATA code appended, e.g.
  // "Emirates (EK)", while the Non-Rev endpoint returns the bare name
  // ("Emirates"). Strip the "(CODE)" suffix so both sides compare equal.
  const normalizeAirlineName = (name?: string) =>
    (name ?? "")
      .replace(/\s*\([^)]*\)\s*/g, " ")
      .trim()
      .toLowerCase();

  // Airlines already in the Non-Rev list, or set as the homebase / primary /
  // additional (working) airlines, cannot also be excluded. Build a lookup
  // of their identifiers (id, airlineId and normalized name).
  const buildAirlineKeys = (airlines: Airline[]) => {
    const ids = new Set<number>();
    const names = new Set<string>();
    airlines.forEach((airline: any) => {
      if (airline?.id != null) ids.add(airline.id);
      if (airline?.airlineId != null) ids.add(airline.airlineId);
      const name = normalizeAirlineName(airline?.airlineName ?? airline?.name);
      if (name) names.add(name);
    });
    return { ids, names };
  };

  const nonRevKeys = useMemo(() => buildAirlineKeys(nonRevAirlines), [nonRevAirlines]);
  const workingAirlineKeys = useMemo(() => buildAirlineKeys(workingAirlines), [workingAirlines]);

  const isNonRevAirline = (option: AirlineOption) =>
    nonRevKeys.ids.has(option.value) ||
    nonRevKeys.names.has(normalizeAirlineName(option.label));

  const isWorkingAirline = (option: AirlineOption) =>
    workingAirlineKeys.ids.has(option.value) ||
    workingAirlineKeys.names.has(normalizeAirlineName(option.label));

  // Options the user may exclude: hide Non-Rev, working airlines and already-excluded ones.
  const availableAirlineOptions = useMemo(() => {
    const excludedValues = new Set(excludedAirlines.map((a) => a.value));
    return airlineOptions.filter(
      (option) =>
        !isNonRevAirline(option) &&
        !isWorkingAirline(option) &&
        !excludedValues.has(option.value),
    );
  }, [airlineOptions, excludedAirlines, nonRevKeys, workingAirlineKeys]);

  const handleSelectChange = (selected: readonly AirlineOption[]) => {
    setSelectedAirlines([...selected]); // Store selection but don't update UI yet
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple clicks
    if (!isDirty) return;

    if (selectedAirlines.length === 0) {
      showToast(
        "Please select at least one airline to exclude.",
        "error",
        3000,
      );
      return;
    }

    // Guard against excluding an airline that is in the Non-Rev list.
    const conflicting = selectedAirlines.filter(isNonRevAirline);
    if (conflicting.length > 0) {
      const names = conflicting.map((a) => a.label).join(", ");
      showToast(
        `${names} cannot be excluded because ${
          conflicting.length > 1 ? "they are" : "it is"
        } in your Non-Rev airlines list.`,
        "error",
        4000,
      );
      return;
    }

    // Guard against excluding a homebase / primary / additional airline.
    const conflictingWorking = selectedAirlines.filter(isWorkingAirline);
    if (conflictingWorking.length > 0) {
      const names = conflictingWorking.map((a) => a.label).join(", ");
      showToast(
        `${names} cannot be excluded because ${
          conflictingWorking.length > 1 ? "they are" : "it is"
        } set as your homebase, primary or additional airline.`,
        "error",
        4000,
      );
      return;
    }

    setIsSaving(true);
    try {
      const selectedIds = selectedAirlines.map((airline) =>
        Number(airline.value),
      );
      await excludeIncludeAirlines(selectedIds, true);

      // Show success toaster

      showToast("Excluded airlines saved successfully!", "success", 3000);
      await fetchExcludedAirlines();
      setSelectedAirlines([]);
      setIsDirty(false);
    } catch (error) {
      console.error("Failed to save excluded airlines", error);
      showToast(
        "Failed to save excluded airlines. Please try again.",
        "error",
        3000,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveAirline = async (id: number) => {
    try {
      await excludeIncludeAirlines([id], false); // Ensure id is a number

      // Update UI immediately
      setExcludedAirlines((prevExcluded) =>
        prevExcluded.filter((airline) => airline.value !== id),
      );

      showToast("Airline removed from exclusion list.", "success");
      setIsDirty(true);
    } catch (error) {
      console.error("Failed to include airline", error);
      showToast("Failed to include airline. Please try again.", "error");
    }
  };

  const handleCancel = async () => {
    const allIds = excludedAirlines.map((airline) => airline.value);
    if (allIds.length === 0) {
      showToast("There are no excluded airlines to remove.", "info", 3000);
      return;
    }
    setIsSaving(true);
    try {
      await excludeIncludeAirlines(allIds, false);
      setExcludedAirlines([]);
      setSelectedAirlines([]);
      setIsDirty(false);
      showToast("All excluded airlines have been removed.", "success", 3000);
    } catch (error) {
      showToast(
        "Failed to remove all excluded airlines. Please try again.",
        "error",
        3000,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flight-search">
      <div className="bg-white p-6 rounded-xl">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
          <i className="st-flight text-grease-100 text-lg"></i>
          <p className="text-grease-100 text-base font-semibold">
            Flight Search
          </p>
        </div>

        {/* Non-Rev Airlines */}
        <p className="text-grease-100 text-sm font-semibold mb-4">
          Airlines You can Non-Rev with
        </p>
        {nonRevAirlines.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {nonRevAirlines.map((nonRevAirline) => (
              <div
                key={nonRevAirline?.id}
                className="px-4 py-2 flex items-center gap-3 border border-gray-200 rounded-lg bg-white"
              >
                <p className="text-sm text-grease-100 font-medium">
                  {nonRevAirline?.airlineName}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center gap-3 text-lightgray-200">
            <i className="st-flight text-gray-400 text-base shrink-0" aria-hidden="true" />
            <p className="text-sm text-lightgray-200">
              No Non-Rev airlines configured yet.
            </p>
          </div>
        )}

        {/* Excluded Airlines */}
        <p className="text-grease-100 text-sm font-semibold mt-8 mb-4">
          Excluded Airlines
        </p>
        <div className="max-w-sm">
          <Select
            options={availableAirlineOptions}
            onInputChange={handleSearchChange}
            onMenuOpen={() => fetchAirlineOptions("")}
            value={selectedAirlines}
            onChange={handleSelectChange}
            placeholder="Search here..."
            isClearable
            isMulti
            className="basic-multi-select"
            classNamePrefix="select"
            styles={customStyles}
            menuShouldScrollIntoView={!isIOSDevice}
          />
        </div>
        {excludedAirlines.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-3">
            {excludedAirlines.map((airline) => (
              <div
                key={airline.value}
                className="px-4 py-2 flex items-center gap-3 border border-gray-200 rounded-lg"
              >
                <p className="text-sm text-grease-100 font-medium">
                  {airline?.label}
                </p>
                <div
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => handleRemoveAirline(airline.value)}
                >
                  <i className="st-close text-gray-400 text-[8px]"></i>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Buttons - kept as-is */}
        <div className="flex items-center justify-end gap-4">
          <button
            className="p-4 w-32 rounded-lg text-lightgray-200 text-center bg-lightgray-100 text-xs mt-6"
            onClick={handleCancel}
          >
            REMOVE ALL
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className={`p-4 w-32 rounded-lg text-xs mt-6 text-white bg-[#FFBC00] ${
              isDirty && !isSaving
                ? "shadow-button cursor-pointer"
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            {isSaving ? "SAVING..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightSearch;
